import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Converts markdown to HTML for PDF export
 * @param {string} markdown - The markdown text to convert
 * @returns {string} - The converted HTML
 */
export const convertMarkdownToHtml = (markdown) => {
    // Process tables first
    let html = markdown;

    // Find and convert tables
    // This regex matches markdown tables with header row, separator row, and data rows
    // Modified to ensure it doesn't capture content after the table
    const tableRegex = /(\|(?:[^|\n]*\|)+)\n\|(?:[-:]+\|)+\n((?:\|(?:[^|\n]*\|)+\n?)*)/gm;

    html = html.replace(tableRegex, (match, headerRow, bodyRows) => {
        // Process header row
        const headers = headerRow.split('|').map(cell => cell.trim()).filter(cell => cell);

        // Process body rows
        const rows = bodyRows.trim().split('\n').map(row => {
            return row.split('|').map(cell => cell.trim()).filter(cell => cell);
        });

        // Build HTML table
        let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin-bottom: 16px;">';

        // Add header row
        tableHtml += '<thead><tr>';
        headers.forEach(header => {
            tableHtml += `<th style="border: 1px solid #ddd; padding: 8px; text-align: left; background-color: rgba(0, 0, 0, 0.1);">${header}</th>`;
        });
        tableHtml += '</tr></thead>';

        // Add body rows
        tableHtml += '<tbody>';
        rows.forEach(row => {
            tableHtml += '<tr>';
            row.forEach(cell => {
                tableHtml += `<td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${cell}</td>`;
            });
            tableHtml += '</tr>';
        });
        tableHtml += '</tbody></table>';

        return tableHtml;
    });

    // Process unordered lists
    // This regex matches unordered lists with - or * or + bullets
    const listRegex = /^([\s]*)[-*+] (.+)$/gm;
    let lastIndentLevel = -1;
    let listHtml = '';
    let inList = false;

    // First pass: convert the list items and track list structure
    html = html.replace(listRegex, (match, indent, content) => {
        const indentLevel = indent.length;

        // Start a new list if we're not in one
        if (!inList) {
            inList = true;
            lastIndentLevel = indentLevel;
            listHtml = `<ul style="margin-left: 20px; margin-bottom: 10px;">
<li>${content}</li>`;
            return '{{LIST_PLACEHOLDER}}';
        }

        // Handle indentation changes
        if (indentLevel > lastIndentLevel) {
            // Start a nested list
            listHtml += `<ul style="margin-left: 20px;">
<li>${content}</li>`;
        } else if (indentLevel < lastIndentLevel) {
            // Close the current list and go back up
            const levelDiff = Math.floor((lastIndentLevel - indentLevel) / 2);
            for (let i = 0; i < levelDiff; i++) {
                listHtml += '</ul>';
            }
            listHtml += `<li>${content}</li>`;
        } else {
            // Same level, just add a new item
            listHtml += `<li>${content}</li>`;
        }

        lastIndentLevel = indentLevel;
        return '';
    });

    // Close any open lists
    if (inList) {
        listHtml += '</ul>';
        html = html.replace('{{LIST_PLACEHOLDER}}', listHtml);
    }

    // Process ordered lists
    const orderedListRegex = /^([\s]*)(\d+)\. (.+)$/gm;
    lastIndentLevel = -1;
    listHtml = '';
    inList = false;

    // First pass: convert the ordered list items and track list structure
    html = html.replace(orderedListRegex, (match, indent, number, content) => {
        const indentLevel = indent.length;

        // Start a new list if we're not in one
        if (!inList) {
            inList = true;
            lastIndentLevel = indentLevel;
            listHtml = `<ol style="margin-left: 20px; margin-bottom: 10px;">
<li>${content}</li>`;
            return '{{ORDERED_LIST_PLACEHOLDER}}';
        }

        // Handle indentation changes
        if (indentLevel > lastIndentLevel) {
            // Start a nested list
            listHtml += `<ol style="margin-left: 20px;">
<li>${content}</li>`;
        } else if (indentLevel < lastIndentLevel) {
            // Close the current list and go back up
            const levelDiff = Math.floor((lastIndentLevel - indentLevel) / 2);
            for (let i = 0; i < levelDiff; i++) {
                listHtml += '</ol>';
            }
            listHtml += `<li>${content}</li>`;
        } else {
            // Same level, just add a new item
            listHtml += `<li>${content}</li>`;
        }

        lastIndentLevel = indentLevel;
        return '';
    });

    // Close any open ordered lists
    if (inList) {
        listHtml += '</ol>';
        html = html.replace('{{ORDERED_LIST_PLACEHOLDER}}', listHtml);
    }

    // Process headings separately first to ensure they're properly detected
    // Using a different approach to ensure each line is processed correctly
    const lines = html.split('\n');
    const processedLines = lines.map(line => {
        // Check for h1
        if (line.match(/^\s*#\s+(.+)$/)) {
            return line.replace(/^\s*#\s+(.+)$/, '<h1 style="font-size: 24px; margin-top: 24px; margin-bottom: 16px; font-weight: bold;">$1</h1>');
        }
        // Check for h2
        else if (line.match(/^\s*##\s+(.+)$/)) {
            return line.replace(/^\s*##\s+(.+)$/, '<h2 style="font-size: 20px; margin-top: 20px; margin-bottom: 14px; font-weight: bold;">$1</h2>');
        }
        // Check for h3
        else if (line.match(/^\s*###\s+(.+)$/)) {
            return line.replace(/^\s*###\s+(.+)$/, '<h3 style="font-size: 18px; margin-top: 18px; margin-bottom: 12px; font-weight: bold;">$1</h3>');
        }
        // Check for h4
        else if (line.match(/^\s*####\s+(.+)$/)) {
            return line.replace(/^\s*####\s+(.+)$/, '<h4 style="font-size: 16px; margin-top: 16px; margin-bottom: 10px; font-weight: bold;">$1</h4>');
        }
        // Check for h5
        else if (line.match(/^\s*#####\s+(.+)$/)) {
            return line.replace(/^\s*#####\s+(.+)$/, '<h5 style="font-size: 14px; margin-top: 14px; margin-bottom: 8px; font-weight: bold;">$1</h5>');
        }
        // Check for h6
        else if (line.match(/^\s*######\s+(.+)$/)) {
            return line.replace(/^\s*######\s+(.+)$/, '<h6 style="font-size: 12px; margin-top: 12px; margin-bottom: 6px; font-weight: bold;">$1</h6>');
        }
        // Return unchanged line if not a heading
        return line;
    });

    html = processedLines.join('\n');

    // Process other markdown elements
    return html

        // Inline formatting
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code style="background-color: #f5f5f5; padding: 2px 4px; border-radius: 3px; font-family: monospace;">$1</code>')

        // Code blocks
        .replace(/```json\n([\s\S]*?)\n```/g, '<pre style="background-color: #f5f5f5; padding: 10px; border-radius: 5px; overflow-x: auto; font-family: monospace; margin: 16px 0;">$1</pre>')
        .replace(/```\n([\s\S]*?)\n```/g, '<pre style="background-color: #f5f5f5; padding: 10px; border-radius: 5px; overflow-x: auto; font-family: monospace; margin: 16px 0;">$1</pre>')

        // Horizontal rule
        .replace(/^---$/gm, '<hr style="border: none; border-top: 1px solid #ccc; margin: 16px 0;">')

        // Links
        .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" style="color: #0366d6; text-decoration: none;">$1</a>')

        // Paragraphs and line breaks - modified to reduce excessive spacing
        .replace(/\n\n+/g, '</p><p>') // Multiple newlines become a paragraph break
        .replace(/\n/g, ' '); // Single newlines become spaces to avoid breaking within paragraphs
};

/**
 * Creates a chart visualization for PDF export
 * @param {Object} chartData - The chart data
 * @returns {HTMLElement} - The chart visualization element
 */
export const createChartVisualization = (chartData) => {
    const chartType = chartData.type.toUpperCase();
    const chartLabels = chartData.data.labels;
    const chartValues = chartData.data.datasets[0].data;
    const chartTitle = chartData.title;

    // Create a container for the chart visualization
    const chartVisual = document.createElement('div');
    chartVisual.style.padding = '15px';
    chartVisual.style.backgroundColor = '#f8f9fa';
    chartVisual.style.borderRadius = '8px';
    chartVisual.style.border = '1px solid #e0e0e0';

    // Add chart header
    const chartHeader = document.createElement('div');
    chartHeader.style.marginBottom = '15px';
    chartHeader.style.borderBottom = '1px solid #e0e0e0';
    chartHeader.style.paddingBottom = '10px';
    chartHeader.innerHTML = `
        <div style="font-weight: bold; font-size: 16px; color: #333;">${chartTitle}</div>
        <div style="font-size: 12px; color: #666; margin-top: 5px;">Chart Type: ${chartType}</div>
    `;
    chartVisual.appendChild(chartHeader);

    // Create a table representation for all chart types
    const tableContainer = document.createElement('div');
    tableContainer.style.marginTop = '10px';

    // Create a table with the chart data
    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.border = '1px solid #ddd';

    // Create header row
    const headerRow = document.createElement('tr');

    // Add header cell for labels
    const labelHeader = document.createElement('th');
    labelHeader.style.padding = '8px';
    labelHeader.style.backgroundColor = '#f1f1f1';
    labelHeader.style.border = '1px solid #ddd';
    labelHeader.style.textAlign = 'left';
    labelHeader.textContent = 'Label';
    headerRow.appendChild(labelHeader);

    // Add header cell for values
    const valueHeader = document.createElement('th');
    valueHeader.style.padding = '8px';
    valueHeader.style.backgroundColor = '#f1f1f1';
    valueHeader.style.border = '1px solid #ddd';
    valueHeader.style.textAlign = 'left';
    valueHeader.textContent = 'Value';
    headerRow.appendChild(valueHeader);

    // Add header cell for visualization
    const visualHeader = document.createElement('th');
    visualHeader.style.padding = '8px';
    visualHeader.style.backgroundColor = '#f1f1f1';
    visualHeader.style.border = '1px solid #ddd';
    visualHeader.style.textAlign = 'left';
    visualHeader.textContent = 'Visualization';
    headerRow.appendChild(visualHeader);

    table.appendChild(headerRow);

    // Colors for the chart
    const colors = ['#4285f4', '#ea4335', '#fbbc05', '#34a853', '#8e44ad', '#3498db', '#e67e22', '#2ecc71'];

    // Find the maximum value for scaling
    const maxValue = Math.max(...chartValues);

    // Add data rows
    chartLabels.forEach((label, index) => {
        const value = chartValues[index];
        const color = colors[index % colors.length];

        // Create row
        const row = document.createElement('tr');

        // Add label cell
        const labelCell = document.createElement('td');
        labelCell.style.padding = '8px';
        labelCell.style.border = '1px solid #ddd';
        labelCell.textContent = label;
        row.appendChild(labelCell);

        // Add value cell
        const valueCell = document.createElement('td');
        valueCell.style.padding = '8px';
        valueCell.style.border = '1px solid #ddd';
        valueCell.textContent = value;
        row.appendChild(valueCell);

        // Add visualization cell
        const visualCell = document.createElement('td');
        visualCell.style.padding = '8px';
        visualCell.style.border = '1px solid #ddd';

        // Create SVG for visualization
        const svgWidth = 150;
        const svgHeight = 20;

        // Create SVG element
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', svgWidth);
        svg.setAttribute('height', svgHeight);
        svg.style.display = 'block';

        // Create rectangle for bar
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        const barWidth = (value / maxValue) * svgWidth;
        rect.setAttribute('x', '0');
        rect.setAttribute('y', '0');
        rect.setAttribute('width', barWidth);
        rect.setAttribute('height', svgHeight);
        rect.setAttribute('fill', color);

        // Add rectangle to SVG
        svg.appendChild(rect);

        // Add SVG to cell
        visualCell.appendChild(svg);
        row.appendChild(visualCell);

        // Add row to table
        table.appendChild(row);
    });

    // Add table to container
    tableContainer.appendChild(table);
    chartVisual.appendChild(tableContainer);

    // Add a simple visual representation based on chart type
    if (chartType === 'PIE') {
        const pieContainer = document.createElement('div');
        pieContainer.style.marginTop = '15px';
        pieContainer.style.textAlign = 'center';
        pieContainer.innerHTML = '<div style="font-style: italic; color: #666;">Pie chart representation</div>';
        chartVisual.appendChild(pieContainer);
    } else if (chartType === 'LINE') {
        const lineContainer = document.createElement('div');
        lineContainer.style.marginTop = '15px';
        lineContainer.style.textAlign = 'center';
        lineContainer.innerHTML = '<div style="font-style: italic; color: #666;">Line chart representation</div>';
        chartVisual.appendChild(lineContainer);
    } else if (chartType === 'RADAR') {
        const radarContainer = document.createElement('div');
        radarContainer.style.marginTop = '15px';
        radarContainer.style.textAlign = 'center';
        radarContainer.innerHTML = '<div style="font-style: italic; color: #666;">Radar chart representation</div>';
        chartVisual.appendChild(radarContainer);
    }

    return chartVisual;
};

/**
 * Extracts chart data from message content
 * @param {string} content - The message content
 * @returns {Array} - Array of chart data objects
 */
export const extractChartData = (content) => {
    const charts = [];

    // Look for JSON chart data in the format ```json\n{...}\n```
    const chartRegex = /```json\s*\n({[\s\S]*?})\s*\n```/g;
    let match;

    while ((match = chartRegex.exec(content)) !== null) {
        try {
            const chartData = JSON.parse(match[1]);

            // Validate that this is chart data with the expected structure
            if (chartData.type &&
                chartData.title &&
                chartData.data &&
                chartData.data.labels &&
                chartData.data.datasets) {

                charts.push({
                    chartData,
                    fullMatch: match[0],
                    startIndex: match.index,
                    endIndex: match.index + match[0].length
                });
            }
        } catch (e) {
            console.error('Error parsing chart JSON:', e);
        }
    }

    return charts;
};

/**
 * Converts chat messages to markdown format
 * @param {Array} messages - The chat messages
 * @param {string} sessionId - The chat session ID
 * @param {string} selectedCountry - The selected country
 * @returns {string} - The markdown representation of the chat
 */
export const convertChatToMarkdown = (messages, sessionId, selectedCountry) => {
    let markdown = `# Data Analysis Chat Export\n\n`;
    markdown += `**Export Date:** ${new Date().toLocaleString()}\n\n`;
    markdown += `**Session ID:** ${sessionId}\n\n`;
    if (selectedCountry) {
        markdown += `**Country:** ${selectedCountry}\n\n`;
    }
    markdown += `---\n\n`;

    messages.forEach((message, index) => {
        const timestamp = new Date(message.timestamp).toLocaleString();
        const role = message.role === 'user' ? '👤 **You**' : '🤖 **Assistant**';

        markdown += `## ${role} - ${timestamp}\n\n`;

        // For assistant messages, check if there are charts
        if (message.role === 'assistant') {
            // Extract chart data
            const charts = extractChartData(message.content);

            if (charts.length > 0) {
                // If there are charts, we'll add placeholders for them
                let lastIndex = 0;
                let modifiedContent = '';

                charts.forEach((chart, chartIndex) => {
                    // Add text before the chart
                    if (chart.startIndex > lastIndex) {
                        modifiedContent += message.content.substring(lastIndex, chart.startIndex);
                    }

                    // Add a placeholder for the chart
                    modifiedContent += `\n\n[CHART: ${chart.chartData.title}]\n\n`;

                    lastIndex = chart.endIndex;
                });

                // Add any remaining text after the last chart
                if (lastIndex < message.content.length) {
                    modifiedContent += message.content.substring(lastIndex);
                }

                markdown += modifiedContent + '\n\n';
            } else {
                markdown += `${message.content}\n\n`;
            }
        } else {
            markdown += `${message.content}\n\n`;
        }

        markdown += `---\n\n`;
    });

    return markdown;
};

/**
 * Creates a temporary div for PDF export
 * @param {Array} messages - The chat messages
 * @param {string} sessionId - The chat session ID
 * @param {string} selectedCountry - The selected country
 * @returns {HTMLElement} - The temporary div element
 */
export const createPdfExportDiv = (messages, sessionId, selectedCountry) => {
    // Create a temporary div to render the chat with charts
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = '800px';
    tempDiv.style.padding = '20px';
    tempDiv.style.fontFamily = 'Arial, sans-serif';
    tempDiv.style.fontSize = '12px';
    tempDiv.style.lineHeight = '1.5';
    tempDiv.style.color = '#000';
    tempDiv.style.backgroundColor = '#fff';

    // Add header
    const headerDiv = document.createElement('div');
    headerDiv.innerHTML = `
        <h1>Data Analysis Chat Export</h1>
        <p><strong>Export Date:</strong> ${new Date().toLocaleString()}</p>
        <p><strong>Session ID:</strong> ${sessionId}</p>
        ${selectedCountry ? `<p><strong>Country:</strong> ${selectedCountry}</p>` : ''}
        <hr style="border: none; border-top: 1px solid #ccc; margin: 20px 0;">
    `;
    tempDiv.appendChild(headerDiv);

    // Process each message
    for (const message of messages) {
        const timestamp = new Date(message.timestamp).toLocaleString();
        const role = message.role === 'user' ? 'You' : 'Assistant';

        // Create message container
        const messageDiv = document.createElement('div');
        messageDiv.style.marginBottom = '20px';
        messageDiv.style.padding = '10px';
        messageDiv.style.borderRadius = '5px';
        messageDiv.style.backgroundColor = message.role === 'user' ? '#e3f2fd' : '#f5f5f5';

        // Add message header
        const headerDiv = document.createElement('div');
        headerDiv.innerHTML = `
            <h2 style="margin-top: 0;">${role} - ${timestamp}</h2>
        `;
        messageDiv.appendChild(headerDiv);

        // For assistant messages, check if there are charts
        if (message.role === 'assistant') {
            // Extract chart data
            const charts = extractChartData(message.content);

            if (charts.length > 0) {
                // If there are charts, split the content and render charts in between
                let lastIndex = 0;

                for (const chart of charts) {
                    // Add text before the chart
                    if (chart.startIndex > lastIndex) {
                        const textBefore = message.content.substring(lastIndex, chart.startIndex);
                        const textDiv = document.createElement('div');
                        textDiv.innerHTML = convertMarkdownToHtml(textBefore);
                        messageDiv.appendChild(textDiv);
                    }

                    // Create a div for the chart
                    const chartDiv = document.createElement('div');
                    chartDiv.style.margin = '15px 0';
                    chartDiv.style.padding = '10px';
                    chartDiv.style.border = '1px solid #ddd';
                    chartDiv.style.borderRadius = '5px';

                    // Add chart title
                    const titleDiv = document.createElement('div');
                    titleDiv.innerHTML = `<h3 style="margin-top: 0;">${chart.chartData.title}</h3>`;
                    chartDiv.appendChild(titleDiv);

                    // Create chart container
                    const chartContainer = document.createElement('div');
                    chartContainer.style.height = '300px';
                    chartContainer.style.width = '100%';
                    chartContainer.className = `chart-container-${charts.indexOf(chart)}`;
                    chartDiv.appendChild(chartContainer);

                    messageDiv.appendChild(chartDiv);

                    lastIndex = chart.endIndex;
                }

                // Add any remaining text after the last chart
                if (lastIndex < message.content.length) {
                    const textAfter = message.content.substring(lastIndex);
                    const textDiv = document.createElement('div');
                    textDiv.innerHTML = convertMarkdownToHtml(textAfter);
                    messageDiv.appendChild(textDiv);
                }
            } else {
                // If no charts, just render the markdown
                const contentDiv = document.createElement('div');
                contentDiv.innerHTML = convertMarkdownToHtml(message.content);
                messageDiv.appendChild(contentDiv);
            }
        } else {
            // For user messages, also render with markdown to maintain consistent styling
            const contentDiv = document.createElement('div');
            contentDiv.innerHTML = convertMarkdownToHtml(message.content);
            messageDiv.appendChild(contentDiv);
        }

        // Add separator
        const separatorDiv = document.createElement('div');
        separatorDiv.innerHTML = '<hr style="border: none; border-top: 1px solid #ccc; margin: 20px 0;">';

        // Add message to temp div
        tempDiv.appendChild(messageDiv);
        tempDiv.appendChild(separatorDiv);
    }

    return tempDiv;
};

/**
 * Renders charts in the temporary div for PDF export
 * @param {HTMLElement} tempDiv - The temporary div element
 * @param {Array} messages - The chat messages
 * @returns {Promise} - Promise that resolves when all charts are rendered
 */
export const renderChartsForPdf = async (tempDiv, messages) => {
    // Extract all charts from all messages
    const allContent = messages.map(m => m.content).join('\n');
    const charts = extractChartData(allContent);

    // Process each chart
    for (let i = 0; i < charts.length; i++) {
        const chartContainer = tempDiv.querySelector(`.chart-container-${i}`);
        if (chartContainer) {
            // Create the chart visualization
            const chartVisual = createChartVisualization(charts[i].chartData);

            // Add the chart visualization to the container
            chartContainer.innerHTML = '';
            chartContainer.appendChild(chartVisual);
        }
    }

    // Return a resolved promise since we don't need to wait for anything
    return Promise.resolve();
};

/**
 * Exports chat to PDF
 * @param {Array} messages - The chat messages
 * @param {string} sessionId - The chat session ID
 * @param {string} selectedCountry - The selected country
 * @param {Function} setExportingPdf - Function to set exporting PDF state
 * @param {Function} setError - Function to set error state
 * @returns {Promise} - Promise that resolves when PDF is exported
 */
export const exportChatToPdf = async (messages, sessionId, selectedCountry, setExportingPdf, setError) => {
    try {
        setExportingPdf(true);

        // Create temporary div for PDF export
        const tempDiv = createPdfExportDiv(messages, sessionId, selectedCountry);
        document.body.appendChild(tempDiv);

        // Render charts in the temporary div
        await renderChartsForPdf(tempDiv, messages);

        // Use html2canvas to convert the HTML to canvas
        const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff'
        });

        // Remove the temporary div
        document.body.removeChild(tempDiv);

        // Create PDF
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 295; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;

        let position = 0;

        // Add the image to PDF
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // Add new pages if content is longer than one page
        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        // Format date for filename
        const formatDateForFilename = (date) => {
            return date.toISOString().split('T')[0];
        };

        // Download the PDF
        const countryPrefix = selectedCountry ? `${selectedCountry}-` : '';
        const filename = `${countryPrefix}chat-export-${formatDateForFilename(new Date())}.pdf`;
        pdf.save(filename);
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        setError('Failed to export chat to PDF. Please try again.');
    } finally {
        setExportingPdf(false);
    }
};
